import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CreateStudentCredentialData,
  CredentialExportRequest,
  UpdateStudentCredentialData,
  studentCredentialsApi,
} from '../student-credentials';

export const studentCredentialKeys = {
  all: ['students', 'credentials'] as const,
  forStudent: (studentId: string) => [...studentCredentialKeys.all, studentId] as const,
};

export function useStudentCredentialsQuery(studentId: string) {
  return useQuery({
    queryKey: studentCredentialKeys.forStudent(studentId),
    queryFn: () => studentCredentialsApi.listForStudent(studentId),
    enabled: Boolean(studentId),
  });
}

export function useCreateStudentCredentialMutation(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateStudentCredentialData) => studentCredentialsApi.create(studentId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: studentCredentialKeys.forStudent(studentId) }),
  });
}

export function useUpdateStudentCredentialMutation(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ credId, data }: { credId: string; data: UpdateStudentCredentialData }) =>
      studentCredentialsApi.update(studentId, credId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: studentCredentialKeys.forStudent(studentId) }),
  });
}

export function useDeleteStudentCredentialMutation(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (credId: string) => studentCredentialsApi.remove(studentId, credId),
    onSuccess: () => qc.invalidateQueries({ queryKey: studentCredentialKeys.forStudent(studentId) }),
  });
}

export function useRevealStudentCredentialMutation(studentId: string) {
  return useMutation({
    mutationFn: (credId: string) => studentCredentialsApi.reveal(studentId, credId),
  });
}

export function useExportCredentialsMutation() {
  return useMutation({
    mutationFn: (body: CredentialExportRequest) => studentCredentialsApi.export(body),
  });
}
